import {
    Deferred,
    EventBus,
    Instance,
    Objekt,
    Script,
    Service,
} from '@siposdani87/sui-js';
import { app } from '../app';
import { ConfigService } from './configService';
import { LanguageService } from './languageService';

export class GoogleAnalytics4Service extends Service {
    static readonly inject = ['instances', 'languageService', 'configService'];

    script: Script;
    eventBus: EventBus;

    constructor(
        instances: Instance,
        private languageService: LanguageService,
        private configService: ConfigService,
    ) {
        super();
        this.script = instances.script;
        this.eventBus = instances.eventBus;
    }

    enter(): void {
        this.eventBus.set('state.change', (state: Objekt) => {
            const url = state.get<string>('url');
            this.sendPageView(url);

            const title = state.get<string>('title');
            const screenName = this.languageService.translate(title);
            this.sendScreenView(screenName);
        });

        this.eventBus.set('module.serviceLoaded', () => {
            const value = Math.round(performance.now());
            this.sendTiming('dependencies', 'load', value);
        });

        this.eventBus.set('module.loaded', () => {
            const value = Math.round(performance.now());
            this.sendTiming('modules', 'load', value);
        });

        this._loadSDK();
    }

    private _loadSDK() {
        const deferred = new Deferred();

        (window as any)['dataLayer'] = (window as any)['dataLayer'] || [];
        (window as any)['gtag'] = (...args: any[]) => {
            (window as any)['dataLayer'].push(args);
        };
        (window as any)['gtag']('js', new Date());
        (window as any)['gtag']('consent', 'default', {
            ad_storage: 'granted',
            analytics_storage: 'granted',
            personalization_storage: 'granted',
            functionality_storage: 'granted',
            security_storage: 'granted',
        });

        this.script
            .load(
                'google-analytics-4',
                'https://www.googletagmanager.com/gtag/js',
                {
                    id: this.configService.get<string>('google.ga4_measurement_id'),
                },
            )
            .then(
                () => {
                    const appName = this.configService.get<string>('app.name');
                    const trackingId = this.configService.get<string>(
                        'google.ga4_measurement_id',
                    );

                    (window as any)['gtag']('config', trackingId, {
                        app_name: appName,
                        send_page_view: false,
                    });
                    deferred.resolve();
                },
                () => {
                    // If bocked by AdBlocker
                    deferred.reject();
                },
            );
        return deferred.promise();
    }

    sendPageView(url: string): void {
        if ((window as any)['gtag']) {
            (window as any)['gtag']('event', 'page_view', {
                // 'page_title': '<Page Title>',
                page_location: url,
            });
        }
    }

    sendScreenView(screenName: string): void {
        if ((window as any)['gtag']) {
            (window as any)['gtag']('event', 'screen_view', {
                screenName: screenName,
            });
        }
    }

    sendException(
        description: string,
        opt_isFatal: boolean | undefined = false,
    ): void {
        if ((window as any)['gtag']) {
            (window as any)['gtag']('event', 'exception', {
                description: description,
                fatal: opt_isFatal,
            });
        }
    }

    sendEvent(
        category: string,
        action: string,
        opt_label: string | undefined,
        opt_value: number | undefined,
    ): void {
        if ((window as any)['gtag']) {
            (window as any)['gtag']('event', action, {
                event_category: category,
                event_label: opt_label,
                value: opt_value,
            });
        }
    }

    sendSocial(network: string, action: string, target: string): void {
        if ((window as any)['gtag']) {
            (window as any)['gtag']('event', 'auth', {
                event_category: network,
                event_label: action,
            });
        }
    }

    sendTiming(
        category: string,
        variable: string,
        value: number,
        opt_label?: string,
    ): void {
        if ((window as any)['gtag']) {
            (window as any)['gtag']('event', 'timing_complete', {
                name: variable,
                value: value,
                event_category: category,
                event_label: opt_label,
            });
        }
    }

    purchaseItem(
        transactionId: string,
        id: string,
        name: string,
        opt_category: string | undefined,
        opt_price: string | undefined,
        opt_currency: string | undefined,
    ): void {
        if ((window as any)['gtag']) {
            (window as any)['gtag']('event', 'purchase', {
                transaction_id: transactionId,
                value: opt_price,
                currency: opt_currency,
                items: [
                    {
                        id,
                        name,
                        category: opt_category,
                        price: opt_price,
                        quantity: 1,
                    },
                ],
            });
        }
    }
}

export const googleAnalytics4Service = app.service('googleAnalytics4Service', GoogleAnalytics4Service);
